import styled, { keyframes } from "styled-components";

/* ===== helpers ===== */
const fadeUp = keyframes`
  0%{opacity:0;transform:translateY(8px)}
  100%{opacity:1;transform:translateY(0)}
`;

const lighten = (hex: string, amount = 0.2) => {
  const c = hex.replace("#", "");
  const n = parseInt(c, 16);
  let r = (n >> 16) & 0xff,
    g = (n >> 8) & 0xff,
    b = n & 0xff;
  const t = (x: number) => Math.min(255, Math.floor(x + (255 - x) * amount));
  r = t(r);
  g = t(g);
  b = t(b);
  const h = (x: number) => x.toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
};

const shimmer = keyframes`
  0%{background-position:-480px 0}
  100%{background-position:480px 0}
`;

export const Container = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  box-shadow: 0 6px 24px rgba(15, 23, 42, 0.07);
  padding: 12px;

  display: flex;
  flex-direction: column;
  gap: 10px;
  animation: ${fadeUp} 300ms ease both;
  position: relative;
  overflow: hidden;
`;

export const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding-bottom: 6px;
  border-bottom: 1px dashed #e2e8f0;
`;

export const Name = styled.div`
  font-weight: 900;
  font-size: 16px;
  color: #0f172a;

  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const UsageRow = styled.div`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  gap: 4px;
  font-weight: 800;
`;

export const Dot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: ${({ $color }) => $color};
  box-shadow: 0 0 0 3px ${({ $color }) => lighten($color, 0.85)};
`;

export const Strong = styled.span`
  font-weight: 900;
  font-size: 13px;
`;
export const Slash = styled.span`
  opacity: 0.5;
  font-weight: 900;
  font-size: 13px;
`;
export const Weak = styled.span`
  color: #64748b;
  font-weight: 800;
  font-size: 13px;
`;

export const MeterRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: center;
`;

export const Meter = styled.div`
  border-radius: 10px;
  background: #eef2f7;
  border-radius: 12px;
  height: 18px;
  overflow: hidden;
  position: relative;
  background-image: linear-gradient(
    90deg,
    rgba(2, 6, 23, 0.06) 1px,
    transparent 1px
  );
  background-size: 40px 100%;
`;

export const Fill = styled.div<{ $pct: number; $color: string }>`
  width: ${({ $pct }) => $pct}%;
  height: 100%;
  background: ${({ $color }) =>
    `linear-gradient(90deg, ${lighten($color, 0.25)}, ${$color})`};
  box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.06);
  transition: width 450ms cubic-bezier(0.22, 0.61, 0.36, 1);
`;

export const PctText = styled.span<{ $color: string }>`
  font-weight: 900;
  font-size: 12px;
  color: ${({ $color }) => $color};
`;

/* ===== Skeletons ===== */
const SkelBase = styled.span`
  display: inline-block;
  border-radius: 6px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 480px 100%;
  animation: ${shimmer} 1.2s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* 이름 자리 */
export const SkelName = styled(SkelBase)`
  width: 140px;
  height: 16px;
`;

/* 사용량/총량 자리 */
export const SkelChip = styled(SkelBase)`
  width: 54px;
  height: 12px;
`;

/* 점 자리 */
export const SkelDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #e2e8f0;
  box-shadow: 0 0 0 3px #f8fafc;
`;

/* 게이지 바 전체 */
export const SkelBar = styled(SkelBase)`
  width: 100%;
  height: 18px;
  border-radius: 12px;
`;

/* 퍼센트 텍스트 자리 */
export const SkelPct = styled(SkelBase)`
  width: 28px;
  height: 12px;
  border-radius: 4px;
`;
